import * as Clipboard from 'expo-clipboard';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ScrollView,
  SafeAreaView,
  ActivityIndicator,
  Platform,
  Image,
  TouchableOpacity,
  Linking,
  StyleSheet,
} from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import SecondaryHeader from '@components/Headers/SecondaryHeader';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/RestyleIcon';
import { useProjectFromQuery } from '@hooks/useProjectFromQuery';
import { CoverPage } from '@screens/Projects/Report/CoverPage';
import { Overview } from '@screens/Projects/Report/Overview';
import { ProjectActivityLog } from '@screens/Projects/Report/ProjectActivityLog';
import { ProjectImages } from '@screens/Projects/Report/ProjectImages';
import { TaskDetail } from '@screens/Projects/Report/TaskDetail';
import theme from '@themes/theme';

import AppLogo from '../../../../assets/images/AppLogo.png';
import {
  useGetProjectReportQuery,
  useGenerateProjectReportMutation,
  useProjectReportCoverPageQuery,
  useProjectReportOverviewQuery,
  useListProjectReportTasksQuery,
  useListProjectMediaQuery,
  useGetProjectChatActivityReportQuery,
} from '../../../graphql/generated';
interface ParamTypes {
  route: {
    params: {
      projectUUId?: string;
      tokenizedUUID?: string;
    };
  };
}

interface CoverPageData {
  team?: {
    avatarFile?: {
      url: string;
    };
    name?: string;
  };
  createdAt?: string;
  generatedBy?: string;
}

const Report = ({ route }: ParamTypes) => {
  const { projectUUId, tokenizedUUID } = route?.params || '';
  const { t } = useTranslation();
  const toast = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [uuid, setUuid] = useState(tokenizedUUID || '');
  const [coverPageData, setCoverPageData] = useState<CoverPageData>({
    team: {
      avatarFile: {
        url: '',
      },
      name: '',
    },
    createdAt: '',
    generatedBy: '',
  });

  const [projectIdFromLink, setProjectIdFromLink] = useState('');
  const [projectId, setProjectId] = useState<string>('');

  const { project } = useProjectFromQuery({
    projectUUId: projectUUId ?? '',
  });

  useEffect(() => {
    setProjectId(project?.id ?? '');
  }, [project]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      const getInitialURL = window.location.href ?? '';
      const getInitialURLParts = getInitialURL.split('=');
      const projectIdFromLink =
        getInitialURLParts[getInitialURLParts?.length - 1];
      setProjectIdFromLink(projectIdFromLink);

      if (projectUUId) {
        setProjectIdFromLink(projectId);
        if (uuid) {
          const baseURL = window.location.origin;
          window.location.href = `${baseURL}/projects/project-report?uuid=${uuid}`;
        }
        return;
      }
      if (tokenizedUUID) {
        setUuid(tokenizedUUID);
      }
    }
  }, [tokenizedUUID, uuid, window?.location?.href]);

  const copyReportPublicLink = async () => {
    toast.show('Public link copied to clipboard');
    await Clipboard.setStringAsync(
      `${process.env.WEB_BASE_URL}/projects/project-report?uuid=${uuid}`
    );
  };

  // Fetch UUID
  const { data, error, loading } = useGetProjectReportQuery({
    variables: {
      id: projectId ?? projectIdFromLink,
    },
    skip: uuid !== '',
  });

  // Fetch Project Media
  const { data: ProjectMedia } = useListProjectMediaQuery({
    variables: {
      uuid: uuid,
    },
    skip: uuid === '',
  });

  // Fetch Project Activity Logs
  const { data: projectActivityLogData } = useGetProjectChatActivityReportQuery(
    {
      variables: {
        uuid: uuid || tokenizedUUID || '',
      },
      skip: !uuid || uuid === '',
    }
  );

  // Create UUID if not exist from above call
  const [generateProjectReport, { data: generateProjectReportData }] =
    useGenerateProjectReportMutation({
      variables: {
        projectId: projectId ?? projectIdFromLink,
      },
    });

  useEffect(() => {
    if (!generateProjectReportData) return;
    setUuid(generateProjectReportData.generateProjectReport?.uuid || '');
  }, [generateProjectReportData]);

  // fetch project report data through UUID
  const {
    data: ProjectData,
    error: errorInCoverPage,
    loading: loadingCoverPage,
  } = useProjectReportCoverPageQuery({
    variables: {
      uuid: uuid || tokenizedUUID || '',
    },
    skip: uuid === '' && !tokenizedUUID,
  });

  const { data: projectOverviewData, error: errorOverviewData } =
    useProjectReportOverviewQuery({
      variables: {
        uuid: uuid || tokenizedUUID || '',
      },
      skip: uuid === '' && !tokenizedUUID,
    });

  const { data: listTasks, error: errorListTasks } =
    useListProjectReportTasksQuery({
      variables: {
        uuid: uuid || tokenizedUUID || '',
      },
      skip: uuid === '' && !tokenizedUUID,
    });

  useEffect(() => {
    if (error && !uuid) {
      generateProjectReport();
      return;
    }
    if (data) {
      setUuid(data?.getProjectReport || '');
    }
  }, [data, error]);

  useEffect(() => {
    if (loading || loadingCoverPage) {
      setIsLoading(true);
      return;
    }

    if (errorInCoverPage) {
      setIsLoading(false);
      return;
    }

    if (ProjectData?.projectReportCoverPage) {
      setCoverPageData(ProjectData?.projectReportCoverPage);
      setIsLoading(false);
    }
  }, [ProjectData, errorInCoverPage, loading, loadingCoverPage]);

  if (isLoading) {
    return (
      <Box flex={1} justifyContent='center' alignContent='center'>
        <ActivityIndicator size='large' color={theme.colors.black} />
      </Box>
    );
  }

  if (
    (errorInCoverPage || errorOverviewData || errorListTasks) &&
    Platform.OS === 'web'
  ) {
    return (
      <Box flex={1} marginTop='xxl' marginLeft='xxl'>
        <Box marginTop='xxl'>
          <Image source={AppLogo} style={styles.appLogo} />
        </Box>
        <Text
          variant='heading1'
          marginVertical='l'
          fontSize={28}
          fontWeight='600'>
          {t('models:projects.report.expired.title')}
        </Text>
        <Box width='50%' alignItems='flex-start'>
          <Text
            textAlign='left'
            marginVertical='l'
            variant='labelLarge'
            fontWeight='500'
            fontSize={18}>
            {t('models:projects.report.expired.description')}
          </Text>
        </Box>
        <TouchableOpacity
          onPress={() =>
            Linking.openURL('https://app.tasktag.com/').catch((err) =>
              console.error('An error occurred', err)
            )
          }
          activeOpacity={0.7}>
          <Box
            backgroundColor='black'
            flexDirection='row'
            alignItems='center'
            justifyContent='center'
            paddingHorizontal='xs'
            paddingVertical='xxs'
            borderRadius='xs'
            height={60}
            width={160}>
            <Text
              variant='buttonLabel'
              fontWeight='500'
              fontSize={16}
              color='white'
              marginRight='xxs'>
              tasktag.com
            </Text>
            <Icon name='ArrowUpRight' variant='l' color='white' />
          </Box>
        </TouchableOpacity>
      </Box>
    );
  }

  return (
    <Box flex={1} justifyContent='center'>
      {Platform.OS !== 'web' && (
        <SecondaryHeader
          title='Project Report'
          showRightIcon={true}
          iconName='Link'
          onPressIcon={copyReportPublicLink}
        />
      )}
      <SafeAreaView style={styles.container}>
        <ScrollView showsVerticalScrollIndicator={false} style={{ flex: 1 }}>
          <Box maxWidth={1280} alignSelf='center' width='100%'>
            <CoverPage data={coverPageData} />
            <Overview
              data={projectOverviewData?.projectReportOverview}
              teamName={coverPageData?.team?.name}
              tasks={listTasks}
            />
            <TaskDetail uuid={uuid} tasks={listTasks} />
            {ProjectMedia?.listProjectMedia &&
              ProjectMedia?.listProjectMedia?.length > 0 && (
                <ProjectImages data={ProjectMedia?.listProjectMedia ?? []} />
              )}
            <ProjectActivityLog
              data={projectActivityLogData?.getProjectChatActivityReport}
            />
          </Box>
        </ScrollView>
      </SafeAreaView>
    </Box>
  );
};

export default Report;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: Platform.OS === 'web' ? 64 : 0,
    marginVertical: Platform.OS === 'web' ? 40 : 0,
  },
  appLogo: {
    alignSelf: 'flex-start',
    width: 350,
    height: 100,
  },
  expiredBtn: {
    backgroundColor: 'black',
  },
});
